import React from 'react';
import { Text } from '@amzn/storm-ui';
import styled from 'styled-components';
import {
  accountSelectionListId,
  containerWithBorderBottomMixin,
  IA2AccountDropdownSearchInput,
  StyledIntroWidgetAnchor,
} from './styles';
import { useAccountDropdownSearch } from './useAccountDropdownSearchAndFilter';
import { IA2AccountDropdownTypeSection } from './IA2AccountDropdownTypeSection';
import { IA2AccountDropdownInfoCard } from './IA2AccountDropdownInfoCard';
import { IA2AccountDropdownFilterInput } from './IA2AccountDropdownFilterInput';
import { IA2AccountFilteredMessage } from './IA2AccountFilteredMessage';
import { FlexBox, FlexContainer } from 'src/commons/components/FlexContainer';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { IA2AccountDropdownFooterLinks } from './IA2AccountDropdownFooterLinks';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getAccountTypeListLabel } from './AccountTypeLocalizationKeyMapping';
import { NoAccountsMessage } from './NoAccountsMessage';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { findSelectedAccountSwitcherAccount } from './accountHelper';
import { ErrorLoadingAccountsMessage } from 'src/features/header/components/AccountDropdown/ErrorLoadingAccountsMessage';

const SEARCH_AND_FILTER_DISPLAY_COUNT = 10;

const AccountSwitcherContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.base}
    ${({ theme }) => theme.spacing.medium};
`;

const AccountListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing.medium};
  border-top: 1px solid ${({ theme }) => theme.palette.mercury};
  ${containerWithBorderBottomMixin}
`;

const AccountDropdownWithSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
`;

const AllAccountsMessageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.base};
  display: flex;
  justify-content: center;
`;
const SearchAndFilterContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.base};
`;

type IA2AccountDropdownProps = {
  advertisingAccountsDataTaktId?: string;
};

export const IA2AccountDropdown: React.VFC<IA2AccountDropdownProps> = ({
  advertisingAccountsDataTaktId,
}) => {
  const {
    accounts,
    currentEntityId,
    accountTypeCounter,
    accountCounter,
    loadingStatus,
  } = useAccountDropdownContext();

  const bundle = useAccountDropdownBundle();
  const chromeConfig = useChromeConfigContext();
  const isAllAccountsPageScope =
    chromeConfig.pageHeader?.pageScope === 'ALL_ACCOUNTS';
  const isFailedLoadingAccountData = loadingStatus === 'error';

  const {
    onSearch,
    onFilter,
    accountTypeList,
    accountTypeFilterList,
    onClear,
    searchValue,
  } = useAccountDropdownSearch({
    accounts,
  });
  const hasMoreThanOneAccountType = accountTypeCounter > 1;
  const hasMoreThanTenAccounts =
    accountCounter > SEARCH_AND_FILTER_DISPLAY_COUNT;

  const accountSearchPlaceholder = bundle.getMessage(
    'account-dropdown-placeholder',
  );

  if (isAllAccountsPageScope) {
    return (
      <AllAccountsMessageContainer>
        <Text>{bundle.getMessage('global-accounts-pill-message')}</Text>
      </AllAccountsMessageContainer>
    );
  }
  const selectedAccount = findSelectedAccountSwitcherAccount(
    currentEntityId,
    accounts.map((accountGroup) => accountGroup.accounts).flat(),
  );

  return (
    <AccountDropdownWithSearchContainer>
      <AccountSwitcherContainer>
        <IA2AccountDropdownInfoCard />
        {hasMoreThanTenAccounts && (
          <SearchAndFilterContainer>
            <FlexContainer>
              <FlexBox flex={1}>
                <IA2AccountDropdownSearchInput
                  data-takt-id="ccx-account-switcher-search"
                  id="account-switcher-search"
                  placeholder={accountSearchPlaceholder}
                  onChange={onSearch}
                  onClear={onClear}
                  fullWidth={true}
                  label={accountSearchPlaceholder}
                  value={searchValue}
                />
              </FlexBox>
              {hasMoreThanOneAccountType && (
                <IA2AccountDropdownFilterInput
                  accountTypes={accountTypeFilterList}
                  onSubmit={onFilter}
                />
              )}
            </FlexContainer>
            {accountTypeFilterList.some((filter) => !filter.isSelected) && (
              <IA2AccountFilteredMessage accountTypeList={accountTypeList} />
            )}
          </SearchAndFilterContainer>
        )}
      </AccountSwitcherContainer>
      <AccountListContainer
        id={accountSelectionListId}
        data-takt-id={advertisingAccountsDataTaktId}
      >
        {isFailedLoadingAccountData && <ErrorLoadingAccountsMessage />}
        {!isFailedLoadingAccountData && accountTypeList.length === 0 && (
          <NoAccountsMessage />
        )}
        {!isFailedLoadingAccountData &&
          accountTypeList.length > 0 &&
          accountTypeList.map((accountTypeItem, index) => (
            <IA2AccountDropdownTypeSection
              key={accountTypeItem.label}
              accounts={accountTypeItem.accounts}
              label={getAccountTypeListLabel(
                accountTypeItem.accountType,
                bundle,
              )}
              type={accountTypeItem.accountType}
              selectedEntityId={selectedAccount?.entityId}
              isLastAccountType={index === accountTypeList.length - 1}
            />
          ))}
      </AccountListContainer>
      {hasMoreThanOneAccountType && (
        <StyledIntroWidgetAnchor id="ia2-app-centric-account-switcher" />
      )}
      <IA2AccountDropdownFooterLinks />
    </AccountDropdownWithSearchContainer>
  );
};
